import { MAX_AGE, MIN_AGE, ageBucket, ageMatchesFilter, bucketsForFilter, normalizeAgeFilter, widenAgeFilter, } from './age.js';
import { Gender, GenderPreference, Topic } from './enums.js';
/**
 * Волна поиска. Пол и тема не ослабляются никогда — расширяется только диапазон
 * возраста, иначе на низком онлайне пользователь ждёт бесконечно.
 */
export const MatchWave = {
    /** Только указанный диапазон. */
    STRICT: 0,
    /** Диапазон расширен на AGE_WAVE_WIDEN_YEARS в обе стороны. */
    WIDENED: 1,
    /** Любой возраст. */
    ANY_AGE: 2,
};
export const AGE_WAVE_WIDEN_YEARS = 5;
export function oppositeGender(gender) {
    return gender === Gender.MALE ? Gender.FEMALE : Gender.MALE;
}
/** Какие варианты «кого ищем» показывать для данной темы и пола. */
export function allowedPreferences(topic, gender) {
    if (topic === Topic.FLIRT) {
        return [oppositeGender(gender)];
    }
    return [GenderPreference.MALE, GenderPreference.FEMALE, GenderPreference.ANY];
}
export function isPreferenceAllowed(topic, gender, preference) {
    return allowedPreferences(topic, gender).includes(preference);
}
/**
 * Приводит выбор пользователя к допустимому значению.
 * Для «Флирта» выбор игнорируется: пол собеседника всегда противоположный —
 * это правило домена, а не подсказка интерфейса, поэтому оно применяется и на
 * сервере, даже если клиент прислал что-то иное.
 */
export function resolvePreferredGender(topic, gender, requested) {
    if (topic === Topic.FLIRT) {
        return oppositeGender(gender);
    }
    return requested ?? GenderPreference.ANY;
}
/** Удовлетворяет ли пол собеседника фильтру. */
export function genderSatisfies(gender, preference) {
    return preference === GenderPreference.ANY || preference === gender;
}
/** Развёртка фильтра в конкретные полы — основа для перебора бакетов очереди. */
export function wantedGenders(preference) {
    if (preference === GenderPreference.ANY) {
        return [Gender.MALE, Gender.FEMALE];
    }
    // После проверки выше в типе остались только MALE | FEMALE.
    return [preference];
}
/** Диапазон возраста с учётом волны. */
export function ageFilterForWave(filter, wave) {
    if (wave >= MatchWave.ANY_AGE) {
        return { min: MIN_AGE, max: MAX_AGE };
    }
    if (wave === MatchWave.WIDENED) {
        return widenAgeFilter(filter, AGE_WAVE_WIDEN_YEARS);
    }
    return normalizeAgeFilter(filter);
}
/** Волна по времени ожидания. */
export function waveForWaitTime(waitedMs, thresholds) {
    if (thresholds.dropAfterMs > 0 && waitedMs >= thresholds.dropAfterMs) {
        return MatchWave.ANY_AGE;
    }
    if (thresholds.relaxAfterMs > 0 && waitedMs >= thresholds.relaxAfterMs) {
        return MatchWave.WIDENED;
    }
    return MatchWave.STRICT;
}
/**
 * Совместимость двух наборов предпочтений. Взаимная по обоим признакам: и пол,
 * и возраст должны устраивать каждую сторону, иначе кто-то получит собеседника,
 * которого не искал.
 *
 * `waveA` / `waveB` — волны каждой из сторон: тот, кто ждёт дольше, ищет шире.
 */
export function arePreferencesCompatible(a, b, waveA = MatchWave.STRICT, waveB = MatchWave.STRICT) {
    if (a.topic !== b.topic) {
        return false;
    }
    if (a.topic === Topic.FLIRT && a.gender === b.gender) {
        return false;
    }
    if (!genderSatisfies(b.gender, a.preferredGender) || !genderSatisfies(a.gender, b.preferredGender)) {
        return false;
    }
    return (ageMatchesFilter(b.age, ageFilterForWave(a.preferredAge, waveA)) &&
        ageMatchesFilter(a.age, ageFilterForWave(b.preferredAge, waveB)));
}
export function areTicketsCompatible(a, b) {
    if (a.userId === b.userId) {
        return false;
    }
    if (a.topic !== b.topic) {
        return false;
    }
    if (a.topic === Topic.FLIRT && a.gender === b.gender) {
        return false;
    }
    if (!genderSatisfies(b.gender, a.preferredGender) || !genderSatisfies(a.gender, b.preferredGender)) {
        return false;
    }
    // У тикета уже посчитан диапазон текущей волны — расширять повторно нельзя.
    return ageMatchesFilter(b.age, a.effectiveAge) && ageMatchesFilter(a.age, b.effectiveAge);
}
// ─── Ключи очереди Redis ─────────────────────────────────────────────────────
// Формула ключа живёт рядом с правилами совместимости, чтобы они не разъехались:
// изменение правила без изменения раскладки по бакетам — источник тихих багов.
export const QUEUE_BUCKET_PREFIX = 'q';
/**
 * Бакет определяется тем, что о пользователе знает встречная сторона: тема, его
 * пол, кого он ищет и корзина его возраста. Точный возраст и точный диапазон
 * проверяются при подборе по тикету.
 */
export function buildQueueBucketKey(prefs) {
    return `${QUEUE_BUCKET_PREFIX}:${prefs.topic}:${prefs.gender}:${prefs.preferredGender}:${ageBucket(prefs.age)}`;
}
/**
 * Бакеты, в которых может лежать подходящий собеседник.
 *
 * Партнёр подходит, если его пол входит в мой фильтр, его фильтр включает мой
 * пол, а его возраст попадает в мой диапазон. Первые два условия закодированы в
 * ключе, третье — в наборе корзин возраста. Количество ключей ограничено
 * сверху (2 пола × 2 варианта фильтра × число корзин диапазона) и не зависит от
 * числа пользователей: подбор не сканирует ни базу, ни пространство ключей.
 */
export function compatibleBucketKeys(prefs, wave = MatchWave.STRICT) {
    const partnerGenders = wantedGenders(prefs.preferredGender);
    const partnerPreferences = [prefs.gender, GenderPreference.ANY];
    const buckets = bucketsForFilter(ageFilterForWave(prefs.preferredAge, wave));
    const keys = [];
    for (const partnerGender of partnerGenders) {
        for (const partnerPreference of partnerPreferences) {
            // При «Флирте» собеседник всегда ищет противоположный пол: бакета с
            // фильтром ANY для этой темы просто не существует.
            if (prefs.topic === Topic.FLIRT && partnerPreference === GenderPreference.ANY) {
                continue;
            }
            for (const bucket of buckets) {
                keys.push(`${QUEUE_BUCKET_PREFIX}:${prefs.topic}:${partnerGender}:${partnerPreference}:${bucket}`);
            }
        }
    }
    return keys;
}
//# sourceMappingURL=matching.js.map